import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/library/command";
import {
  COUNTRY_DIAL_CODES,
  DEFAULT_COUNTRY_CODE,
} from "@/constants/countryCodes";

interface CountryCodeComboboxProps {
  value: string | undefined;
  onChange: (value: string) => void;
  /** Visual states surfaced by react-hook-form. */
  hasError?: boolean;
  /** Optional sizing override; matches our Select trigger by default. */
  triggerClassName?: string;
  /** Read-only mode — the picker won't open. */
  disabled?: boolean;
}

/**
 * Searchable country dialing-code picker. Visually matches the surrounding
 * phone input (white background, ~h-10, w-[110px]). Search matches against
 * the country name, ISO region code, and the dial code itself — typing
 * "ind", "in", "+91", or "91" all surface India.
 */
export default function CountryCodeCombobox({
  value,
  onChange,
  hasError,
  triggerClassName,
  disabled,
}: CountryCodeComboboxProps) {
  const [open, setOpen] = useState(false);
  const selected =
    COUNTRY_DIAL_CODES.find((c) => c.code === (value ?? DEFAULT_COUNTRY_CODE)) ??
    COUNTRY_DIAL_CODES.find((c) => c.code === DEFAULT_COUNTRY_CODE)!;

  return (
    <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn(
            "h-10 w-[110px] flex-shrink-0 inline-flex items-center justify-between gap-2 rounded-[12px] border bg-white px-3 text-sm outline-none transition-[box-shadow] focus-visible:ring-[3px] focus-visible:ring-ring/50 cursor-pointer",
            hasError ? "border-red-500" : "border-input",
            disabled && "cursor-not-allowed opacity-60 bg-[#f4f6f5]",
            triggerClassName,
          )}
        >
          <span className="truncate">{selected.code}</span>
          <ChevronDown className="size-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[260px] p-0 rounded-[12px]"
      >
        <Command
          // Default cmdk filter is a fuzzy substring match on `value` +
          // any `keywords` we attach below, which is exactly what we want
          // (typing "+91", "91", "ind", or "india" should all hit IN).
        >
          <CommandInput placeholder="Search country or code…" />
          <CommandList className="max-h-[260px]">
            <CommandEmpty>No country found.</CommandEmpty>
            {COUNTRY_DIAL_CODES.map((c) => {
              const isSelected = c.code === selected.code;
              const labelOnly = c.label.replace(/^\+\d+\s*/, "");
              return (
                <CommandItem
                  key={c.region}
                  // value drives cmdk's matcher; keep it stable + unique
                  value={`${c.label} ${c.region}`}
                  // keywords adds extra search tokens (digits without `+`,
                  // bare country name, territories sharing the code) without
                  // affecting the rendered label
                  keywords={[c.code.replace("+", ""), labelOnly, ...(c.keywords ?? [])]}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="truncate">{c.label}</span>
                  {isSelected && (
                    <Check className="size-4 shrink-0 opacity-80" />
                  )}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
